<template>
	<header class="kova-lp-header">
		<p class="kova-lp-header__ribbon">{{ ribbon }}</p>

		<div class="kova-lp-header__panel">
			<span aria-hidden="true" class="kova-lp-header__vignette" />

			<span aria-hidden="true" class="kova-lp-header__glow" />

			<div class="kova-lp-header__bar">
				<picture>
					<source media="(min-width: 1024px)" :srcset="logoDark" />
					<img alt="Kova" class="kova-lp-header__logo" :src="logoLime" />
				</picture>

				<button class="kova-lp-header__cta" type="button" @click="onCtaClick">{{ ctaLabel }}</button>
			</div>

			<div class="kova-lp-header__rating">
				<div class="kova-lp-header__rating-score">
					<LandingKovaStars class="kova-lp-header__stars" />
					<p class="kova-lp-header__rating-value">{{ rating.score }}&nbsp; {{ rating.label }}</p>
				</div>
				<p class="kova-lp-header__rating-caption">{{ rating.caption }}</p>
			</div>
		</div>

		<!-- Mobile "FREE DELIVERY" stamp. Outside the panel because it hangs 22px below it
		     and the panel clips its glow ellipses. Switched on, and given its artwork, by
		     the header rules in styles/kova-landing.scss. -->
		<span aria-hidden="true" class="kova-lp-header__badge" />
	</header>
</template>

<script>
import { useOnboardingStepEvents } from '@/composables/useOnboardingStepEvents'
import LandingKovaStars from '@/modules/Landing/components/LandingKovaStars.vue'
import logoDark from '@/assets/kova/landing/logo-kova-dark.svg'
import logoLime from '@/assets/kova/logo-light-green.svg'

export default {
	name: 'LandingKovaHeader',

	components: { LandingKovaStars },

	props: {
		ctaLabel: {
			type: String,
			required: true,
		},

		// { score, label, caption } — shown inside the header on mobile only; the
		// desktop frame puts the same block in the hero column (LandingKovaHero).
		rating: {
			type: Object,
			required: true,
		},

		ribbon: {
			type: String,
			required: true,
		},
	},

	// progressLoaderCompleted is LandingView's generic "advance into the funnel"
	// channel — it carries the entry_point that lands on OnboardingWelcomeDone.
	emits: ['progressLoaderCompleted'],

	setup() {
		const { jetButtonClick } = useOnboardingStepEvents()
		return { jetButtonClick, logoDark, logoLime }
	},

	methods: {
		onCtaClick() {
			// Tracked here rather than through LandingView's LANDING_CTA_BLOCK_NAMES: that map
			// exists to pair a CTA with the block it sits in, and per AL-12446 #99131 the header
			// is not a tracked block — nothing emits OnboardingBlockView for it. So the click
			// goes out with no `block_name` (the field is optional in the schema, same as the
			// delegated privacy/terms links), instead of naming a block that can never be joined.
			this.jetButtonClick('CHECK_MY_ELIGIBILITY')
			this.$emit('progressLoaderCompleted', 'header_eligible')
		},
	},
}
</script>

<style lang="scss" scoped>
.kova-lp-header {
	position: relative;
	width: 100%;
	font-family: var(--kova-lp-font);
}

.kova-lp-header__badge {
	display: none; // mobile /app landings only — see styles/kova-landing.scss
}

.kova-lp-header__ribbon {
	box-sizing: border-box;
	margin: 0;
	padding: var(--size-12) var(--size-10);

	font-size: var(--size-16);
	line-height: var(--size-20);

	color: var(--kova-lp-hero-on-panel);
	background: var(--kova-lp-hero-ribbon);
	text-align: center;
}

.kova-lp-header__panel {
	box-sizing: border-box;
	position: relative;
	overflow: hidden;

	display: flex;
	flex-direction: column;
	gap: var(--size-20);
	padding: var(--size-16) var(--size-8) 0;

	background: var(--kova-lp-hero-panel);
}

.kova-lp-header__vignette {
	position: absolute;
	left: -145px;
	top: -44px;
	width: 664px;
	height: 216px;

	border-radius: 50%;
	background: var(--kova-lp-hero-shade);
	filter: blur(100px);
	mask-image: linear-gradient(to bottom, #000 55%, transparent 100%);
}

.kova-lp-header__glow {
	position: absolute;
	left: 140px;
	// The ellipse starts 120px above the seam (panel y 10 vs the 130px header);
	// anchoring to the bottom keeps that true if the header measures differently.
	bottom: -360px;
	width: 400px;
	height: 480px;

	border-radius: 50%;
	background: var(--kova-lp-hero-glow);
	filter: blur(80px);
}

// With the burger gone the logo is the only thing here, and the newest frame
// (5108:49368) centres it.
.kova-lp-header__bar {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	padding-inline: var(--size-32);
}

.kova-lp-header__logo {
	display: block;
	width: 121px;
	height: var(--size-28);
}

// Desktop-only nav CTA (4841:21108).
.kova-lp-header__cta {
	display: none;
}

.kova-lp-header__rating {
	position: relative;
	box-sizing: border-box;

	display: flex;
	flex-direction: column;
	align-items: center;
	gap: var(--size-2);
	padding: var(--size-8) var(--size-20);

	border-radius: var(--size-16);
	background: var(--kova-lp-surface);
}

.kova-lp-header__rating-score {
	display: flex;
	align-items: center;
	gap: var(--size-8);
}

.kova-lp-header__stars {
	height: 15px; // 5 stars + 4 x 2px gaps = the 88px row from Figma 4843:27556
}

.kova-lp-header__rating-value {
	margin: 0;

	font-size: var(--size-14);
	font-weight: 600;
	line-height: 1.4;

	color: var(--kova-lp-ink);
	white-space: nowrap;
}

.kova-lp-header__rating-caption {
	margin: 0;

	font-size: var(--size-14);
	line-height: 1.4;

	color: var(--kova-lp-text-soft);
	text-align: center;
}

// The desktop frame (5026:7484) is a different composition, not a wider mobile
// one: light page, dark logo, a "Get started" pill instead of the burger, and the
// rating block moves out of the header into the hero column. 1024px rather than
// the repo-wide 768px because the desktop hero needs ~1280px of content width
// before its two columns stop colliding.
@media (min-width: 1024px) {
	.kova-lp-header__ribbon {
		padding: var(--size-16) var(--size-10);
		font-size: var(--size-18);
		line-height: var(--size-20);
	}

	// Rectangle 4253679 (5026:7479) — the soft white halo behind the nav. Painted as
	// the panel's own background rather than an overlay: the shape is a ~2% white
	// wash (#FCFAF9 over the page's #F7F4EF, measured off the frame) that fades out
	// around the nav's bottom edge, so a gradient reproduces it exactly while an
	// absolutely-positioned sprite could only ever be clipped flat by the panel.
	.kova-lp-header__panel {
		gap: 0;
		padding: 0;
		background: linear-gradient(to bottom, rgba(255, 255, 255, 0.7) 55%, rgba(255, 255, 255, 0) 100%);
	}

	.kova-lp-header__vignette,
	.kova-lp-header__glow {
		display: none;
	}

	.kova-lp-header__bar {
		// width is explicit because the auto margins that centre the bar would
		// otherwise stop it stretching inside the column flex panel.
		width: 100%;
		max-width: 1440px;
		margin-inline: auto;
		justify-content: space-between; // desktop keeps the logo left, CTA right
		padding: var(--size-24) 46px var(--size-32);
	}

	.kova-lp-header__logo {
		width: 190px;
		height: var(--size-52);
	}

	.kova-lp-header__cta {
		position: relative;

		display: flex;
		align-items: center;
		justify-content: center;
		padding: var(--size-20) var(--size-42);

		font-family: inherit;
		font-size: var(--size-20);
		font-weight: 500;
		line-height: normal;

		border: none;
		border-radius: var(--size-24);
		color: var(--Fill-White);
		background: var(--kova-lp-brand);
		cursor: pointer;
		white-space: nowrap;
	}

	.kova-lp-header__rating {
		display: none;
	}
}
</style>
